import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Boxes, Plus } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import LotPlaceFilter from '../../components/nursery/LotPlaceFilter';
import { compareSlots, slotWhere } from '../../utils/nurseryRowSlots';
import type { NurseryLot, LotStage, PlaceFilter } from '../../types/nursery';

const STAGES: LotStage[] = ['sown', 'seedling', 'transplanted', 'grafted', 'budded', 'growing', 'potted', 'ready', 'dispatched'];

function LotList() {
  const orgTz = useOrgTimezone();
  const navigate = useNavigate();
  const [lots, setLots] = useState<NurseryLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>('');
  const [place, setPlace] = useState<PlaceFilter>({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    nurseryService.getLots({ ...(stage ? { stage } : {}), ...place })
      .then(res => { if (res.success) setLots(res.data || []); else setError('Failed to load lots'); })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [stage, place]);

  const filtered = Boolean(stage || place.farm_id);
  // Read down the page as the rows are walked: row, then position along it.
  const sorted = useMemo(
    () => [...lots].sort((a, b) => compareSlots(a, b) || (a.cultivar_name ?? '').localeCompare(b.cultivar_name ?? '')),
    [lots],
  );

  return (
    <>
      <PageHeader
        title="Lots"
        subtitle="Stock growing across the nursery"
        icon={<Boxes className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" size="sm" onClick={() => navigate('/nursery/lots/new')}>
            <Plus className="h-4 w-4" /> Place lot
          </Button>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <select value={stage} onChange={e => setStage(e.target.value)} aria-label="Stage"
            className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All stages</option>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <LotPlaceFilter value={place} onChange={setPlace} />
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>}

        {loading ? (
          <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-12 bg-neutral-100 rounded-lg animate-pulse" />)}</div>
        ) : lots.length === 0 && filtered ? (
          <p className="text-center py-16 text-neutral-500">No lots match these filters.</p>
        ) : lots.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Boxes className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No lots yet</p>
            <Button variant="primary" className="mt-4" onClick={() => navigate('/nursery/lots/new')}>
              <Plus className="h-4 w-4" /> Place your first lot
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 text-left text-xs">
                <tr>
                  <th className="px-4 py-2 font-medium">Cultivar</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Stage</th>
                  <th className="px-4 py-2 font-medium">Location</th>
                  <th className="px-4 py-2 font-medium text-right">Count</th>
                  <th className="px-4 py-2 font-medium">Ready date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sorted.map(lot => (
                  <tr key={lot.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-2">
                      <Link to={`/nursery/lots/${lot.id}`} className="text-primary-600 hover:underline">
                        {lot.cultivar_name}{lot.rootstock_name ? ` / ${lot.rootstock_name}` : ''}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-neutral-600">{lot.tree_type_code}</td>
                    <td className="px-4 py-2 text-neutral-600">{lot.current_stage}</td>
                    <td className="px-4 py-2 text-neutral-600">{lot.location_name ? slotWhere(lot) : '—'}</td>
                    <td className="px-4 py-2 text-right font-medium text-neutral-800">{lot.current_count.toLocaleString()}</td>
                    {/* The expected date. A lot already at the ready stage is ready now,
                        whether or not anyone recorded a date for it. */}
                    <td className="px-4 py-2 text-neutral-600">
                      {lot.ready_date ? formatLocalDateInTz(lot.ready_date, orgTz)
                        : lot.current_stage === 'ready' ? 'Now' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default LotList;
